import React, { useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLocalizedTemplates } from '../data/templates';
import './TemplatePage.css';

/** Extract a 2D preview from celldata (first `maxRows` rows). */
function buildPreview(sheetData: any[], maxRows = 8) {
  const celldata: any[] = sheetData[0]?.celldata ?? [];
  if (!celldata.length) return { headers: [] as string[], rows: [] as string[][] };

  const maxC = celldata.reduce((m: number, c: any) => Math.max(m, c.c), 0);

  const grid: string[][] = [];
  for (const c of celldata) {
    if (c.r > maxRows) continue;
    if (!grid[c.r]) grid[c.r] = new Array(maxC + 1).fill('');
    const val = c.v?.f ? c.v.f : (c.v?.m ?? c.v?.v ?? '');
    grid[c.r][c.c] = String(val);
  }

  const headers = grid[0] ?? [];
  const rows = grid.slice(1).filter(Boolean);
  return { headers, rows };
}

/** Convert celldata to CSV string. */
function toCsv(sheetData: any[]): string {
  const celldata: any[] = sheetData[0]?.celldata ?? [];
  if (!celldata.length) return '';

  let maxR = 0;
  let maxC = 0;
  for (const c of celldata) {
    if (c.r > maxR) maxR = c.r;
    if (c.c > maxC) maxC = c.c;
  }

  const grid: string[][] = Array.from({ length: maxR + 1 }, () =>
    new Array(maxC + 1).fill('')
  );
  for (const c of celldata) {
    grid[c.r][c.c] = String(c.v?.m ?? c.v?.v ?? '');
  }

  return grid
    .map((row) => row.map((v) => (v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v)).join(','))
    .join('\n');
}

const TemplatePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('landing');

  const template = useMemo(() => {
    if (!slug) return undefined;
    return getLocalizedTemplates(i18n.language).find(tpl => tpl.slug === slug);
  }, [slug, i18n.language]);
  const preview = useMemo(() => (template ? buildPreview(template.sheetData) : null), [template]);

  /* SEO: set document title + meta description */
  React.useEffect(() => {
    if (!template) return;
    document.title = t(template.seoTitleKey);
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', t(template.seoDescKey));
    return () => {
      document.title = 'AI Reports';
    };
  }, [template, t]);

  const handleOpenEditor = useCallback(() => {
    if (!template) return;
    navigate('/editor', {
      state: {
        templateData: template.sheetData,
        templateName: t(template.nameKey),
      },
    });
  }, [navigate, template, t]);

  const handleDownloadCsv = useCallback(() => {
    if (!template) return;
    const csv = toCsv(template.sheetData);
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.slug}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [template]);

  if (!template) {
    return (
      <div className="template-page template-not-found">
        <h1>Template not found</h1>
        <Link to="/templates">Back to templates</Link>
      </div>
    );
  }

  return (
    <div className="template-page">
      <div className="template-page-hero">
        <Link to="/templates" className="template-back-link">
          <i className="fas fa-arrow-left"></i> {t('templates.page_title')}
        </Link>
        <div className="template-page-hero-icon">
          <i className={template.icon}></i>
        </div>
        <h1>{t(template.nameKey)}</h1>
        <p className="template-page-desc">{t(template.descKey)}</p>
        <div className="template-page-actions">
          <button className="template-btn-primary" onClick={handleOpenEditor}>
            <i className="fas fa-table"></i> {t('templates.open_in_editor')}
          </button>
          <button className="template-btn-secondary" onClick={handleDownloadCsv}>
            <i className="fas fa-download"></i> {t('templates.download_csv')}
          </button>
        </div>
      </div>

      {preview && preview.headers.length > 0 && (
        <div className="template-preview-section">
          <h2>{t('templates.preview')}</h2>
          <div className="template-preview-wrapper">
            <table className="template-preview-table">
              <thead>
                <tr>
                  {preview.headers.map((h, i) => (
                    <th key={i}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="template-page-bottom-cta">
        <button className="template-btn-primary" onClick={handleOpenEditor}>
          <i className="fas fa-table"></i> {t('templates.open_in_editor')}
        </button>
      </div>
    </div>
  );
};

export default TemplatePage;
